import axios from "axios";
import { API_URL, ID_TOKEN } from "../config/constants";
import Consultas from "../views/consultas";
import { formatCurrency } from "../utils/converts";
import ConsultasProdutos from "../views/consultas-produtos";
import ConsultasOndemand from "../views/consultas-ondemand";
import GerenciarConsultasOndemand from "../views/consultas-ondemand/gerenciar";

const resetPermissionPath = (newPermission, userPermissions) => {
  const index = userPermissions.findIndex((e) => e.path === newPermission.path);
  for (let p of ["c", "r", "u", "d"]) {
    if (newPermission.permissions[p]) {
      userPermissions[index].permissions[p] = true;
    }
  }
};

const setPermissions = (user) => {
  const groups = user.group;
  const userPermissions = [];

  for (let p of groups.permission) {
    const { path, permissions, onlyWhiteLabel, onlyWhiteLabelPrimary } = p;
    const newPermission = { path, permissions, onlyWhiteLabel, onlyWhiteLabelPrimary };
    const exists = userPermissions.find((e) => e.path === path);
    if (exists) {
      resetPermissionPath(newPermission, userPermissions);
    } else {
      userPermissions.push(newPermission);
    }
  }

  return userPermissions;
};

const organizeMenuOndemandQueries = (queries, clientId) => {
  const arr = [];
  let hasPermissionToManage = false;
  for (let q of queries) {
    if (q.clientsPermission.includes(clientId) && hasPermissionToManage === false) {
      hasPermissionToManage = true;
      arr.push({
        path: "/consultas-ondemand/gerenciar",
        name: "Gerenciar",
        icon: "fas fa-bars-progress",
        component: GerenciarConsultasOndemand,
      });
    }

    const categoria = arr.find((a) => a.name === q.type);
    const item = {
      path: "/consultas-ondemand/" + q.code,
      name: q.name,
      icon: q.icon,
      component: ConsultasOndemand,
      price: `R$ ${formatCurrency(q.basePrice)}`,
    };

    if (categoria) {
      categoria.subchild.push(item);
    } else {
      const cstate = q.type.replace(" ", "_").toLowerCase();

      arr.push({
        collapse: true,
        name: q.type,
        icon: "fas fa-display",
        cstate: cstate,
        subchild: [item],
      });
    }
  }

  for (const item of arr) {
    if (item.subchild) {
      item.subchild.sort((a, b) => (a.name > b.name ? 1 : -1));
    }
  }

  return arr;
};

const organizeMenuProductQueries = (queries) => {
  const arr = [];
  for (let q of queries) {
    if (q.enablePortal) {
      const categoria = arr.find((a) => a.name === q.type);
      const item = {
        path: "/combo-consultas/" + q.code,
        name: q.name,
        icon: q.icon,
        component: ConsultasProdutos,
      };

      if (categoria) {
        categoria.subchild.push(item);
      } else {
        const cstate = q.type.replace(" ", "_").toLowerCase();

        arr.push({
          collapse: true,
          name: q.type,
          icon: q.typeIcon,
          cstate: cstate,
          subchild: [item],
        });
      }
    }
  }
  arr.sort((a, b) => (a.name > b.name ? 1 : -1));
  for (const item of arr) {
    item.subchild.sort((a, b) => (a.name > b.name ? 1 : -1));
  }
  return arr;
};

const organizeMenuQueries = (queries, clientPrices) => {
  const arr = [];
  for (let q of queries) {
    if (q && q.enablePortal) {
      const categoria = arr.find((a) => a.name === q.type);
      const clientPrice = clientPrices.find((price) => price.query === q._id);

      if (categoria) {
        categoria.subchild.push({
          _id: q._id,
          path: "/consulta/" + q.code,
          name: q.name,
          icon: q.icon,
          component: Consultas,
          baseCost: `R$ ${formatCurrency(q.baseCost)}`,
          price: `R$ ${formatCurrency(clientPrice.price)}`,
        });
      } else {
        const cstate = q.type.replace(" ", "_").toLowerCase();

        arr.push({
          collapse: true,
          name: q.type,
          icon: q.typeIcon,
          cstate: cstate,
          subchild: [
            {
              _id: q._id,
              path: "/consulta/" + q.code,
              name: q.name,
              icon: q.icon,
              component: Consultas,
              baseCost: `R$ ${formatCurrency(q.baseCost)}`,
              price: `R$ ${formatCurrency(clientPrice.price)}`,
              isBlocked: q.isBlocked,
            },
          ],
        });
      }
    }
  }
  arr.sort((a, b) => (a.name > b.name ? 1 : -1));
  for (const item of arr) {
    item.subchild.sort((a, b) => (a.name > b.name ? 1 : -1));
  }
  return arr;
};

const getProfileData = async () => {
  const res = await axios.get(`${API_URL}user/get-infos-profile`);
  const response = res.data;

  const user = response.user;
  const queries = response.queries;
  const permissionQueries = response.permissionQueries;
  const queryGroups = response.queryGroups;
  const ondemandQueries = response.ondemandQueries;
  const clientQueries = response.clientQueries;
  const childrenClients = response.childrenClients;
  const termsAccepted = response.termsAccepted;

  return { user, queries, permissionQueries, queryGroups, ondemandQueries, clientQueries, childrenClients, termsAccepted };
};

export const updateProfileStore = async () => {
  const profileData = await getProfileData();
  const user = profileData.user;
  const permissionsQueries = profileData.permissionQueries;
  const queries = profileData.queries;
  const queryGroups = profileData.queryGroups;
  const permissionsOndemandQueries = profileData.ondemandQueries;
  const clientPrice = profileData.clientQueries;
  const childrenClients = profileData.childrenClients;
  const termsAccepted = profileData.termsAccepted;

  await setClient(JSON.stringify(user.client));
  const permissions = setPermissions(user);
  const menuQueries = organizeMenuQueries(permissionsQueries, clientPrice.queries);
  const menuProductQueries = organizeMenuProductQueries(queryGroups);
  const menuOndemandQueries = organizeMenuOndemandQueries(permissionsOndemandQueries, user.client._id);

  return {
    ...profileData.user,
    permissions,
    permissionsQueries: permissionsQueries,
    permissionsOndemandQueries: permissionsOndemandQueries,
    menuQueries,
    menuProductQueries,
    menuOndemandQueries,
    queries,
    queryGroups,
    clientPrice,
    clientChildren: childrenClients,
    termsAccepted
  };
};

export const decodeToken = async () => {
  const res = await axios.get(`${API_URL}user/login/decode`);
  const decoded = res.data;
  return decoded;
};

export const getToken = async () => {
  const token = await localStorage.getItem(ID_TOKEN);
  axios.defaults.headers.authorization = token;
  axios.defaults.headers["x-portal"] = "1";
  return token;
};

export const setToken = async (token) => {
  axios.defaults.headers.authorization = token;
  axios.defaults.headers["x-portal"] = "1";
  return localStorage.setItem(ID_TOKEN, token);
};

export const unsetToken = async () => {
  axios.defaults.headers.authorization = null;
  axios.defaults.headers["x-portal"] = "0";
  return localStorage.removeItem(ID_TOKEN);
};

export const setClient = async (client) => {
  return localStorage.setItem("client", client);
};

export const getClient = () => {
  const client = localStorage.getItem("client");
  return client;
};

export const setUserPre = async (userPre) => {
  return localStorage.setItem("userPre", JSON.stringify(userPre));
};

export const unsetUserPre = async () => {
  return localStorage.removeItem("userPre");
};

export const getUserPre = () => {
  const userPre = localStorage.getItem("userPre");
  return JSON.parse(userPre) ?? null;
};

export const isLogged = async () => {
  return getToken();
};

export const getConfigAwsS3 = async () => {
  const res = await axios.get(`${API_URL}user/get-aws-iam-token-s3`);
  const result = res.data;
  return result;
};

export const getTokenInvite = async (token) => {
  const res = await axios.get(`${API_URL}user/get-invite/${token}`, {
    validateStatus: function (status) {
      return true;
    },
  });
  const result = res.data;
  return result;
};

export const login = async (data) => {
  const res = await axios.post(`${API_URL}user/login`, data, {
    validateStatus: function (status) {
      return true;
    },
  });
  if (!res.data.error) {
    await setToken(res.data.token);
  }
  return res.data;
};

export const requestTokenChangePass = async (data) => {
  const res = await axios.post(`${API_URL}user/request-token-change-pass`, data, {
    validateStatus: function (status) {
      return true;
    },
  });
  return res.data;
};

export const validateTokenChangePass = async (data) => {
  const res = await axios.post(`${API_URL}user/validate-token-change-pass`, data, {
    validateStatus: function (status) {
      return true;
    },
  });
  return res.data;
};

export const changePassByToken = async (data) => {
  const res = await axios.post(`${API_URL}user/change-pass-by-token`, data, {
    validateStatus: function (status) {
      return true;
    },
  });
  return res.data;
};
